import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Divider,
  Stack,
  Avatar,
  Chip,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Visibility, VisibilityOff, Person as PersonIcon } from "@mui/icons-material";
import axiosInstance from "utilities/axios";
import { useAuth } from "context/AuthContext";
import useDialog from "hooks/useDialog";

const ORG_ROLE_LABEL: Record<string, string> = {
  member:   "თანამშრომელი",
  head:     "დეპ. უფროსი",
};

const USER_TYPE_LABEL: Record<string, string> = {
  agency:     "სააგენტო",
  monitoring: "მონიტ. კომპანია",
  admin:      "ადმინი",
};

const PERMISSION_LABEL: Record<string, string> = {
  USER_MANAGE:                    "მომხმარებლების მართვა",
  MANAGE_MONITORING_COMPANIES: "კომპანიების მართვა",
  SUBMIT_MONITORING_FORMS:     "ფორმების შევსება",
  REVIEW_MONITORING:           "მონიტორინგის განხილვა",
  REASSIGN_APPLICATIONS:       "განაცხადის გადამისამართება",
  MANAGE_PAYMENTS:             "გადახდების მართვა",
  MANAGE_MONITORING_AGREEMENTS: "განაცხადის დამატება",
};

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <Typography variant="caption" fontWeight={700} textTransform="uppercase" letterSpacing={0.8} color="text.secondary">
      {children}
    </Typography>
  );
}

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const { showSnackbar } = useDialog();

  // Name form
  const [name, setName]         = useState(user?.name || "");
  const [savingName, setSaving] = useState(false);

  // Password form
  const [oldPassword, setOldPassword]     = useState("");
  const [newPassword, setNewPassword]     = useState("");
  const [confirmPassword, setConfirm]     = useState("");
  const [showOld, setShowOld]             = useState(false);
  const [showNew, setShowNew]             = useState(false);
  const [savingPass, setSavingPass]       = useState(false);

  async function handleSaveName() {
    if (!name.trim() || name === user?.name) return;
    setSaving(true);
    try {
      const res = await axiosInstance.put("/auth/profile", { name: name.trim() });
      showSnackbar(res.data.message || "სახელი განახლდა", "success");
      refreshUser();
    } catch (err: any) {
      showSnackbar(err.response?.data?.message || "შეცდომა", "error");
    } finally {
      setSaving(false);
    }
  }

  async function handleChangePassword() {
    if (!oldPassword || !newPassword || !confirmPassword) return;
    if (newPassword !== confirmPassword) {
      showSnackbar("პაროლები არ ემთხვევა", "error");
      return;
    }
    setSavingPass(true);
    try {
      const res = await axiosInstance.put("/auth/profile", { password: newPassword, oldPassword });
      showSnackbar(res.data.message || "პაროლი შეიცვალა", "success");
      setOldPassword(""); setNewPassword(""); setConfirm("");
    } catch (err: any) {
      showSnackbar(err.response?.data?.message || "შეცდომა", "error");
    } finally {
      setSavingPass(false);
    }
  }

  if (!user) return null;

  const initials = (user.name || "").split(" ").slice(0, 2).map((n) => n[0] || "").join("").toUpperCase();

  return (
    <Box sx={{ p: 3, maxWidth: 640, mx: "auto" }}>
      <Typography variant="h5" fontWeight={700} mb={3}>პროფილი</Typography>

      {/* Identity card */}
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Box display="flex" alignItems="center" gap={2} mb={2.5}>
          <Avatar sx={{ width: 56, height: 56, bgcolor: "primary.main", fontSize: 20 }}>
            {initials || <PersonIcon />}
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={600} lineHeight={1.2}>{user.name}</Typography>
            <Typography variant="body2" color="text.secondary">{user.email}</Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Stack spacing={1.5}>
          <Box display="flex" gap={1} alignItems="center">
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>ტიპი</Typography>
            <Chip label={USER_TYPE_LABEL[user.user_type] || user.user_type} size="small" color={user.user_type === "monitoring" ? "secondary" : "primary"} variant="outlined" sx={{ fontSize: 12 }} />
          </Box>
          <Box display="flex" gap={1} alignItems="center">
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>პოზიცია</Typography>
            <Typography variant="body2">{ORG_ROLE_LABEL[user.org_role] || user.org_role}</Typography>
          </Box>
          {user.organization && (
            <Box display="flex" gap={1} alignItems="center">
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>ორგანიზაცია</Typography>
              <Typography variant="body2">{user.organization.name}</Typography>
            </Box>
          )}
          {user.department && (
            <Box display="flex" gap={1} alignItems="center">
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>დეპარტამენტი</Typography>
              <Typography variant="body2">{user.department.title}</Typography>
            </Box>
          )}
          {user.permissions.length > 0 && (
            <Box display="flex" gap={1} alignItems="flex-start">
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100, pt: 0.5 }}>უფლებები</Typography>
              <Box display="flex" flexWrap="wrap" gap={0.5}>
                {user.permissions.map((p) => (
                  <Chip key={p} label={PERMISSION_LABEL[p] || p} size="small" variant="outlined" sx={{ fontSize: 11, height: 20 }} />
                ))}
              </Box>
            </Box>
          )}
        </Stack>
      </Paper>

      {/* Change name */}
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <SectionTitle>სახელის შეცვლა</SectionTitle>
        <Stack spacing={2} sx={{ mt: 1.5 }}>
          <TextField
            size="small"
            label="სახელი და გვარი"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
          />
          <Box display="flex" justifyContent="flex-end">
            <Button variant="contained" disableElevation size="small" onClick={handleSaveName} disabled={savingName || !name.trim() || name === user.name}>
              შენახვა
            </Button>
          </Box>
        </Stack>
      </Paper>

      {/* Change password */}
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
        <SectionTitle>პაროლის შეცვლა</SectionTitle>
        <Stack spacing={2} sx={{ mt: 1.5 }}>
          <TextField
            size="small"
            label="მიმდინარე პაროლი"
            type={showOld ? "text" : "password"}
            value={oldPassword}
            onChange={(e) => setOldPassword(e.target.value)}
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setShowOld(!showOld)} edge="end">
                    {showOld ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            size="small"
            label="ახალი პაროლი"
            type={showNew ? "text" : "password"}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setShowNew(!showNew)} edge="end">
                    {showNew ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            size="small"
            label="გაიმეორეთ ახალი პაროლი"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirm(e.target.value)}
            fullWidth
            error={!!confirmPassword && confirmPassword !== newPassword}
            helperText={!!confirmPassword && confirmPassword !== newPassword ? "პაროლები არ ემთხვევა" : ""}
          />
          <Typography variant="caption" color="text.secondary">
            მინ. 8 სიმბოლო
          </Typography>
          <Box display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              disableElevation
              size="small"
              onClick={handleChangePassword}
              disabled={savingPass || !oldPassword || !newPassword || !confirmPassword || newPassword !== confirmPassword}
            >
              პაროლის შეცვლა
            </Button>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
}
